import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useState, type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonLink } from '../../../components/ButtonLink';
import { useRequestMagicLinkMutation } from '../../../lib/auth';
import { SupportPrompt } from '../components/SupportPrompt';

const RESEND_SECONDS = 54;

export function CheckEmailPage(): ReactElement {
    const { t } = useTranslation();
    const { email } = useSearch({ from: '/split-hero-layout/find-account/check-email' });
    const requestMagicLink = useRequestMagicLinkMutation();
    const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);

    useEffect((): (() => void) | undefined => {
        if (secondsLeft <= 0) {
            return undefined;
        }
        const timer = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [secondsLeft]);

    return (
        <Stack spacing={3}>
            <Typography variant="h5" component="h1">
                {t('AUTH.CHECK_EMAIL.TITLE')}
            </Typography>

            <Stack spacing={2}>
                <Alert severity="info">
                    <AlertTitle>{t('AUTH.CHECK_EMAIL.LOGIN_LINK_SENT')}</AlertTitle>
                    <Trans
                        i18nKey="AUTH.CHECK_EMAIL.LOGIN_LINK_BODY"
                        values={{ email }}
                        components={{ strong: <strong /> }}
                    />
                </Alert>

                <Stack spacing={1.5}>
                    <ButtonLink variant="outlined" color="primary" fullWidth to="/login">
                        {t('AUTH.CHECK_EMAIL.USE_DIFFERENT_EMAIL')}
                    </ButtonLink>
                    <Typography variant="caption" color="text.secondary">
                        {secondsLeft > 0 ? (
                            t('AUTH.CHECK_EMAIL.RESEND_IN', { seconds: secondsLeft })
                        ) : (
                            <Trans
                                i18nKey="AUTH.CHECK_EMAIL.RESEND_AVAILABLE"
                                components={{
                                    resendLink: (
                                        <Link
                                            component="button"
                                            type="button"
                                            onClick={() => {
                                                setSecondsLeft(RESEND_SECONDS);
                                                requestMagicLink.mutate({ email });
                                            }}
                                        />
                                    ),
                                }}
                            />
                        )}
                    </Typography>
                </Stack>

                <SupportPrompt />
            </Stack>
        </Stack>
    );
}
